import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';
import { Router } from '@angular/router';
import CustomerService from '../../../../services/CustomerService';
import { Customer } from '../../../../models/Customer';
import { LoadingService } from '../../../../services/LoadingService';
import { PageHeaderComponent } from "../../../page-header/page-header.component";

@Component({
  selector: 'app-pending-customers',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatCardModule,
    MatSnackBarModule,
    MatDialogModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    PageHeaderComponent
],
  templateUrl: './pending-customers.component.html',
  styleUrls: ['./pending-customers.component.css']
})
export class PendingCustomersComponent implements OnInit {
  allCustomers: Customer[] = [];
  filteredCustomers: Customer[] = [];
  displayedColumns: string[] = ['select', 'customerNo', 'email', 'name', 'registrationDate', 'status', 'actions'];
  selection = new SelectionModel<Customer>(true, []);

  // Filter properties
  searchText: string = '';
  filterCustomerNo: string = '';
  filterStatus: string = 'Pending'; // Default to Pending

  constructor(
    private customerService: CustomerService,
    private snackBar: MatSnackBar,
    private router: Router,
    private dialog: MatDialog,
    private loading: LoadingService
  ) {}

  ngOnInit(): void {
    this.loadAllCustomers();
  }

  loadAllCustomers(): void {
    this.loading.start('route');
    this.customerService.getRegisteredCustomers().subscribe({
      next: (customers) => {
        this.allCustomers = customers;
        this.applyFilters();
        this.loading.end('route');
        console.log('All customers loaded:', customers);
      },
      error: (error) => {
        console.error('Error loading customers:', error);
        this.loading.end('route');
        this.snackBar.open('Fehler beim Laden der Kunden', 'OK', { duration: 3000 });
      }
    });
  }

  applyFilters(): void {
    let filtered = [...this.allCustomers];

    // Filter by status
    if (this.filterStatus && this.filterStatus !== 'All') {
      filtered = filtered.filter(c => c.registrationStatus === this.filterStatus);
    }

    // Filter by search text (email or name)
    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(c =>
        (c.userDetails?.email?.toLowerCase().includes(search)) ||
        (c.userDetails?.firstName?.toLowerCase().includes(search)) ||
        (c.userDetails?.lastName?.toLowerCase().includes(search))
      );
    }

    // Filter by customer number
    if (this.filterCustomerNo) {
      const customerNo = this.filterCustomerNo;
      filtered = filtered.filter(c =>
        c.reflexCustomerNo?.toString().includes(customerNo)
      );
    }

    this.filteredCustomers = filtered;
  }

  clearFilters(): void {
    this.searchText = '';
    this.filterCustomerNo = '';
    this.filterStatus = 'Pending'; // Reset to default
    this.applyFilters();
  }

  approveCustomer(customer: Customer): void {
    if (!customer.id) return;

    const name = customer.userDetails?.firstName && customer.userDetails?.lastName
      ? `${customer.userDetails.firstName} ${customer.userDetails.lastName}`
      : customer.userDetails?.email || 'Unbekannt';

    if (confirm(`Möchten Sie den Kunden "${name}" (${customer.reflexCustomerNo}) wirklich freischalten?`)) {
      this.customerService.approveCustomer(customer.id).subscribe({
        next: () => {
          this.snackBar.open('Kunde erfolgreich freigeschaltet!', 'OK', { duration: 3000 });
          this.loadAllCustomers(); // Refresh list
        },
        error: (error) => {
          console.error('Error approving customer:', error);
          this.snackBar.open('Fehler beim Freischalten des Kunden', 'OK', { duration: 3000 });
        }
      });
    }
  }

  rejectCustomer(customer: Customer): void {
    if (!customer.id) return;

    const name = customer.userDetails?.firstName && customer.userDetails?.lastName
      ? `${customer.userDetails.firstName} ${customer.userDetails.lastName}`
      : customer.userDetails?.email || 'Unbekannt';

    if (confirm(`Möchten Sie den Kunden "${name}" (${customer.reflexCustomerNo}) wirklich ablehnen?`)) {
      this.customerService.rejectCustomer(customer.id).subscribe({
        next: () => {
          this.snackBar.open('Kunde wurde abgelehnt', 'OK', { duration: 3000 });
          this.loadAllCustomers(); // Refresh list
        },
        error: (error) => {
          console.error('Error rejecting customer:', error);
          this.snackBar.open('Fehler beim Ablehnen des Kunden', 'OK', { duration: 3000 });
        }
      });
    }
  }

  deactivateCustomer(customer: Customer): void {
    if (!customer.id) return;

    const name = customer.userDetails?.firstName && customer.userDetails?.lastName
      ? `${customer.userDetails.firstName} ${customer.userDetails.lastName}`
      : customer.userDetails?.email || 'Unbekannt';

    if (confirm(`Möchten Sie den Kunden "${name}" (${customer.reflexCustomerNo}) wirklich deaktivieren?\n\nDies wird das Konto in Entra ID deaktivieren.`)) {
      this.loading.start('route');
      this.customerService.deactivateCustomer(customer.id).subscribe({
        next: () => {
          this.loading.end('route');
          this.snackBar.open('Kunde wurde erfolgreich deaktiviert', 'OK', { duration: 3000 });
          this.loadAllCustomers(); // Refresh list
        },
        error: (error) => {
          console.error('Error deactivating customer:', error);
          this.loading.end('route');
          this.snackBar.open('Fehler beim Deaktivieren des Kunden', 'OK', { duration: 3000 });
        }
      });
    }
  }

  activateCustomer(customer: Customer): void {
    if (!customer.id) return;

    const name = customer.userDetails?.firstName && customer.userDetails?.lastName
      ? `${customer.userDetails.firstName} ${customer.userDetails.lastName}`
      : customer.userDetails?.email || 'Unbekannt';

    if (confirm(`Möchten Sie den Kunden "${name}" (${customer.reflexCustomerNo}) wirklich aktivieren?\n\nDies wird das Konto in Entra ID aktivieren.`)) {
      this.loading.start('route');
      this.customerService.activateCustomer(customer.id).subscribe({
        next: () => {
          this.loading.end('route');
          this.snackBar.open('Kunde wurde erfolgreich aktiviert', 'OK', { duration: 3000 });
          this.loadAllCustomers(); // Refresh list
        },
        error: (error) => {
          console.error('Error activating customer:', error);
          this.loading.end('route');
          this.snackBar.open('Fehler beim Aktivieren des Kunden', 'OK', { duration: 3000 });
        }
      });
    }
  }

  resendApprovalEmail(customer: Customer): void {
    if (!customer.id) return;

    const name = customer.userDetails?.firstName && customer.userDetails?.lastName
      ? `${customer.userDetails.firstName} ${customer.userDetails.lastName}`
      : customer.userDetails?.email || 'Unbekannt';
    const email = customer.userDetails?.email || 'Unbekannt';

    if (confirm(`Möchten Sie die Freischaltungs-E-Mail an "${name}" (${email}) erneut senden?`)) {
      this.loading.start('route');
      this.customerService.resendApprovalEmail(customer.id).subscribe({
        next: () => {
          this.loading.end('route');
          this.snackBar.open('Freischaltungs-E-Mail wurde erfolgreich versendet', 'OK', { duration: 3000 });
        },
        error: (error) => {
          console.error('Error resending approval email:', error);
          this.loading.end('route');
          this.snackBar.open('Fehler beim Versenden der E-Mail', 'OK', { duration: 3000 });
        }
      });
    }
  }

  deleteCustomer(customer: Customer): void {
    if (!customer.id) return;

    const name = customer.userDetails?.firstName && customer.userDetails?.lastName
      ? `${customer.userDetails.firstName} ${customer.userDetails.lastName}`
      : customer.userDetails?.email || 'Unbekannt';

    if (confirm(`WARNUNG: Möchten Sie den Kunden "${name}" (${customer.reflexCustomerNo}) wirklich LÖSCHEN?\n\nDies wird:\n- Das Entra ID Konto permanent löschen\n- Den Kunden in der Datenbank als gelöscht markieren\n\nDiese Aktion kann nicht rückgängig gemacht werden!`)) {
      this.loading.start('route');
      this.customerService.deleteCustomer(customer.id).subscribe({
        next: () => {
          this.loading.end('route');
          this.snackBar.open('Kunde wurde erfolgreich gelöscht', 'OK', { duration: 3000 });
          this.loadAllCustomers(); // Refresh list
        },
        error: (error) => {
          console.error('Error deleting customer:', error);
          this.loading.end('route');
          this.snackBar.open('Fehler beim Löschen des Kunden', 'OK', { duration: 3000 });
        }
      });
    }
  }

  viewCustomerDetails(customer: Customer): void {
    if (customer.reflexCustomerNo) {
      this.router.navigate(['/admin/customers', customer.reflexCustomerNo]);
    }
  }

  getStatusColor(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'warn';
      case 'unlocked':
        return 'primary';
      case 'rejected':
        return 'accent';
      default:
        return '';
    }
  }

  getStatusLabel(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'Ausstehend';
      case 'unlocked':
        return 'Freigeschaltet';
      case 'rejected':
        return 'Abgelehnt';
      case 'deactivated':
        return 'Deaktiviert';
      default:
        return status || 'Unbekannt';
    }
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.filteredCustomers.filter(c => c.registrationStatus === 'Pending').length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.filteredCustomers.filter(c => c.registrationStatus === 'Pending'));
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: Customer): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id}`;
  }

  approveBulk(): void {
    const selectedIds = this.selection.selected.map(c => c.id).filter(id => id !== undefined) as number[];

    if (selectedIds.length === 0) {
      this.snackBar.open('Bitte wählen Sie mindestens einen Kunden aus', 'OK', { duration: 3000 });
      return;
    }

    if (confirm(`Möchten Sie ${selectedIds.length} Kunden wirklich freischalten?`)) {
      this.loading.start('route');
      this.customerService.approveBulk(selectedIds).subscribe({
        next: (response) => {
          this.loading.end('route');
          this.snackBar.open(
            `${response.successCount} Kunden freigeschaltet, ${response.failureCount} fehlgeschlagen`,
            'OK',
            { duration: 5000 }
          );
          this.selection.clear();
          this.loadAllCustomers(); // Refresh list
        },
        error: (error) => {
          console.error('Error approving bulk:', error);
          this.loading.end('route');
          this.snackBar.open('Fehler beim Freischalten der Kunden', 'OK', { duration: 3000 });
        }
      });
    }
  }

  rejectBulk(): void {
    const selectedIds = this.selection.selected.map(c => c.id).filter(id => id !== undefined) as number[];

    if (selectedIds.length === 0) {
      this.snackBar.open('Bitte wählen Sie mindestens einen Kunden aus', 'OK', { duration: 3000 });
      return;
    }

    if (confirm(`Möchten Sie ${selectedIds.length} Kunden wirklich ablehnen?`)) {
      this.loading.start('route');
      this.customerService.rejectBulk(selectedIds).subscribe({
        next: (response) => {
          this.loading.end('route');
          this.snackBar.open(
            `${response.successCount} Kunden abgelehnt, ${response.failureCount} fehlgeschlagen`,
            'OK',
            { duration: 5000 }
          );
          this.selection.clear();
          this.loadAllCustomers(); // Refresh list
        },
        error: (error) => {
          console.error('Error rejecting bulk:', error);
          this.loading.end('route');
          this.snackBar.open('Fehler beim Ablehnen der Kunden', 'OK', { duration: 3000 });
        }
      });
    }
  }
}
